import StarIcon from '@mui/icons-material/Star';
import { Box, Chip, Grid2 as Grid, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ProductDetail } from '../../api/business/types';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useT } from '../../i18n/LanguageContext';

type ProductClassificationSectionProps = {
  product: ProductDetail;
};

const Block = ({ label, children }: { label: string; children: ReactNode }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Grid>
);

/**
 * Where the product sits in the company's taxonomy (R0030 WP-06): its categories with the main one starred, its
 * brand, its tags and its features — a translation missing in a content language marked. Each category, the
 * brand and a tag lead to the products list filtered by it.
 */
export const ProductClassificationSection = ({ product }: ProductClassificationSectionProps) => {
  const t = useT();
  const navigate = useNavigate();
  const { languages } = useContentLanguages();
  const none = (text: string) => (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('classification.title')}
      </Typography>
      <Grid container spacing={2.5}>
        <Block label={t('classification.categories')}>
          {product.categories.length === 0 ? (
            none(t('classification.noCategories'))
          ) : (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {product.categories.map((category) => (
                <Chip
                  key={category.id}
                  size="small"
                  color={category.main ? 'primary' : 'default'}
                  variant={category.active ? 'filled' : 'outlined'}
                  icon={category.main ? <StarIcon fontSize="small" /> : undefined}
                  label={category.path.join(' › ')}
                  title={category.main ? t('classification.main') : undefined}
                  onClick={() => navigate(`/products?categoryId=${category.id}`)}
                />
              ))}
            </Box>
          )}
        </Block>
        <Block label={t('classification.brand')}>
          {product.brand === null ? (
            none(t('classification.noBrand'))
          ) : (
            <Chip
              size="small"
              label={product.brand.name}
              onClick={() => navigate(`/products?brandId=${product.brand?.id}`)}
            />
          )}
        </Block>
        <Block label={t('classification.tags')}>
          {product.tags.length === 0 ? (
            none(t('classification.noTags'))
          ) : (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {product.tags.map((tag) => (
                <Chip
                  key={tag.id}
                  size="small"
                  variant="outlined"
                  label={tag.name}
                  onClick={() => navigate(`/products?tagId=${tag.id}`)}
                />
              ))}
            </Box>
          )}
        </Block>
        <Block label={t('classification.features')}>
          {product.features.length === 0 ? (
            none(t('classification.noFeatures'))
          ) : (
            <Stack spacing={0.5}>
              {product.features.map((feature) => (
                <Typography key={feature.groupId} variant="body2">
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {feature.groupName}:
                  </Box>{' '}
                  {feature.value}
                  {feature.custom ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('classification.ownText')}
                      sx={{ ml: 1 }}
                    />
                  ) : null}
                  <MissingLanguagesChip
                    missing={missingLanguages(textsOf(feature.translations, 'value', languages), languages)}
                  />
                </Typography>
              ))}
            </Stack>
          )}
        </Block>
      </Grid>
    </Paper>
  );
};
